import type {
  FulfillmentOrderDetail,
  FulfillmentPendingParcel,
  FulfillmentTracking,
} from '@/infrastructure/FulfillmentAdminClient';
import {
  DELEONEX_CARRIER,
  DELEONEX_NEEDS_ATTENTION,
  currentOutboundShipmentOf,
  deleoLabelRefusalOf,
  isDeleoParcel,
  isPendingParcel,
  type OutboundParcel,
} from '@/utils/fulfillmentDeleoLabel';

export const MISSING_OUTBOUND_LABEL =
  'Carrier label missing — create a carrier order and obtain its label before outbound.';
export const UNVERIFIED_OUTBOUND_LABEL = 'Carrier label could not be verified — refresh the order before outbound.';

const hasText = (value: string | null | undefined): boolean => Boolean(value?.trim());

const isLocallyDrawableDeleo = (row: OutboundParcel): boolean => {
  if (row.carrier.trim().toUpperCase() === DELEONEX_CARRIER) {
    return !deleoLabelRefusalOf(row);
  }
  return !isPendingParcel(row) && hasText(row.number) && (hasText(row.carrierOrderId) || hasText(row.labelUrl));
};

/**
 * Only the order's own outbound parcel counts, never an inbound waybill or another order's label.
 * Other carriers (including legacy imports without carrierOrderId) must have returned a label.
 */
export const outboundCarrierBlock = (
  orderId: number,
  tracking: FulfillmentTracking[] = [],
  pendingParcels: FulfillmentPendingParcel[] = [],
): string => {
  const parcel = currentOutboundShipmentOf(tracking, orderId, pendingParcels);
  if (parcel && isPendingParcel(parcel) && parcel.needsAttention !== false) {
    return DELEONEX_NEEDS_ATTENTION;
  }
  if (!parcel) {
    return MISSING_OUTBOUND_LABEL;
  }
  return isDeleoParcel(parcel)
    ? isLocallyDrawableDeleo(parcel)
      ? ''
      : MISSING_OUTBOUND_LABEL
    : !isPendingParcel(parcel) && hasText(parcel.carrier) && hasText(parcel.number) && hasText(parcel.labelUrl)
      ? ''
      : MISSING_OUTBOUND_LABEL;
};

/**
 * Older/external parcels can have an archived label without a labelUrl on the tracking row.
 * Use the same archive read as Bulk Print Labels; booking mode alone never proves a label exists.
 * Read sequentially within an order (queue checks already bound concurrent orders).
 */
export const withArchivedOutboundLabels = async (
  orderId: number,
  tracking: FulfillmentTracking[],
  readLabel: (orderId: number, trackingNumber: string) => Promise<string>,
  pendingParcels: FulfillmentPendingParcel[] = [],
): Promise<FulfillmentTracking[]> => {
  const parcel = currentOutboundShipmentOf(tracking, orderId, pendingParcels);
  if (parcel && isPendingParcel(parcel)) {
    return tracking;
  }
  const resolved: FulfillmentTracking[] = [];
  for (const row of tracking) {
    if (
      row !== parcel ||
      !hasText(row.carrier) ||
      !hasText(row.number) ||
      isDeleoParcel(row) ||
      hasText(row.labelUrl)
    ) {
      resolved.push(row);
      continue;
    }
    try {
      const labelUrl = await readLabel(orderId, row.number);
      resolved.push(labelUrl?.trim() ? { ...row, labelUrl } : row);
    } catch {
      // Missing or unreadable archive: keep the gate closed, without hiding the rest of Order Detail.
      resolved.push(row);
    }
  }
  return resolved;
};

/** A local preflight refusal, kept distinct from an HTTP/transport error for visible action feedback. */
export class OutboundCarrierGateError extends Error {}

type ReadOrder = (orderId: number) => Promise<FulfillmentOrderDetail>;

/** A read failure is a closed gate, not permission to ship. Also used immediately before writes. */
export const readOutboundCarrierBlock = async (orderId: number, readOrder: ReadOrder): Promise<string> => {
  try {
    const detail = await readOrder(orderId);
    return detail.header.orderId === orderId
      ? outboundCarrierBlock(orderId, detail.tracking, detail.pendingParcels)
      : UNVERIFIED_OUTBOUND_LABEL;
  } catch {
    return UNVERIFIED_OUTBOUND_LABEL;
  }
};

/** The queue has no tracking columns; enrich it from the existing detail read, with bounded fan-out. */
export const withOutboundCarrierGates = async <T extends { orderId: number; violations: string[] }>(
  orders: T[],
  readOrder: ReadOrder,
): Promise<T[]> => {
  const result = [...orders];
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(6, orders.length) }, async () => {
      while (next < orders.length) {
        const index = next++;
        const order = orders[index]!;
        const block = await readOutboundCarrierBlock(order.orderId, readOrder);
        result[index] = { ...order, violations: [...(order.violations ?? []), ...(block ? [block] : [])] };
      }
    }),
  );
  return result;
};
